import { Tag as AntdTag } from 'antd';

import { Theme } from '@src/theme/types';
import { ETagIntents, ETagToneIntents } from '@src/typings';
import { TagVariants } from '@src/utilities/tagVariants';
import { twMerge } from 'tailwind-merge';
import { useTheme, withTheme } from '../ThemeProvider';
import { TagProps } from './types';

const TagComponent = ({
    icon,
    format,
    size,
    tone,
    className,
    children,
    ...props
}: TagProps) => {
    const { theme } = useTheme();
    const classes = TagVariants({ format, size, tone });
    const isLinear = format === ETagIntents.linear;
    const isLight = format === ETagIntents.light;

    const Icon = icon ?? (() => null);

    // Map tone enum values to theme property names
    const getToneColor = (toneValue: ETagToneIntents | undefined) => {
        if (!toneValue) return '';

        // Convert tone value to color intent name
        const intentMap: Record<ETagToneIntents, keyof Theme['colors']> = {
            [ETagToneIntents.Natural]: 'neutral',
            [ETagToneIntents.Positive]: 'success',
            [ETagToneIntents.Negative]: 'error',
            [ETagToneIntents.Warning]: 'warning',
            [ETagToneIntents.Primary]: 'primary',
        };

        const intent = intentMap[toneValue];
        if (!intent) return '';

        // For filled tags, use main.filled
        // For light tags, use light.text
        // For linear tags, use main.text
        if (isLinear) {
            return theme.colors[intent].main.filled;
        } else if (isLight) {
            return theme.colors[intent].light.text;
        } else {
            return theme.colors[intent].main.filled;
        }
    };

    // Generate styles based on theme
    const getTagStyle = () => {
        const styles: React.CSSProperties = {};

        if (isLinear) {
            styles.backgroundColor = theme.colors.secondary.light.contentBg;
            styles.color = tone
                ? getToneColor(tone) || theme.colors.primary.light.contentBg
                : theme.colors.primary.light.contentBg;
            styles.borderColor = tone
                ? getToneColor(tone) || theme.colors.primary.main.filled
                : theme.colors.primary.main.filled;
        } else if (isLight) {
            const baseColor = tone
                ? getToneColor(tone) || theme.colors.primary.light.contentBg
                : theme.colors.primary.light.contentBg;
            styles.backgroundColor = `${baseColor}14`; // 14 is hex for 8% opacity
            styles.color = tone
                ? getToneColor(tone) || theme.colors.primary.light.contentBg
                : theme.colors.primary.light.contentBg;
        } else {
            // Filled tag
            styles.backgroundColor = tone
                ? getToneColor(tone) || theme.colors.primary.main.border
                : theme.colors.primary.main.border;
            styles.color = theme.colors.primary.main.text;
        }

        return styles;
    };

    return (
        <AntdTag
            className={twMerge(
                'inline-flex gap-1 px-2 items-center',
                classes,
                className
            )}
            style={getTagStyle()}
            {...props}
        >
            <Icon />
            {children}
        </AntdTag>
    );
};

export const Tag = withTheme(TagComponent);
